import {provide} from "@lit/context";
import {LitElement, html, type PropertyValues} from "lit";
import {customElement, property} from "lit/decorators.js";
import {unsafeStatic} from "lit/static-html.js";

import {ROOT_TAG} from "../globals";
import type {
  BridgeBarConfig,
  ConfigUserLink,
  Language,
  SidebarVariant,
  Theme,
  UserDataEntry
} from "./BridgeBarConfig";
import {BridgeBarConfigContext} from "./BridgeBarConfigContext";
import {
  hasDealerLabelConflict,
  normalizeBridgeBarConfig
} from "./normalizeBridgeBarConfig";
import {setLanguage} from "../i18n";
import type {Partition} from "@common-core/web-component-runtime/runtime";

const tagName = `${ROOT_TAG}-config-provider`;
export const BridgeBarConfigProvider = unsafeStatic(tagName);

/**
 * Provides BridgeBarConfigContext to all descendant consumers.
 *
 * A new config object is created in willUpdate() on every property change
 * to satisfy @lit/context reference-equality change detection (F-10).
 */
@customElement(tagName)
export class BridgeBarConfigProviderElement extends LitElement {
  @property()
  applicationId: string = "";

  @property()
  sessionId: string = "";

  @property()
  deployment: string = "live";

  @property()
  partition: Partition = "prod";

  @property()
  width: string = "";

  @property()
  theme: Theme = "light";

  @property()
  language: Language = "en";

  @property({type: Array})
  userLinks: ConfigUserLink[] = [];

  @property({type: Array})
  userData: UserDataEntry[] = [];

  @property({attribute: false})
  dealerLabelHtml: string = "";

  @property({attribute: false})
  useMockData: boolean = false;

  @property({attribute: false})
  getTracker: (() => unknown) | undefined = undefined;

  @property()
  contextType?: string;

  @property()
  sidebarVariant: SidebarVariant = "fixed";

  @provide({context: BridgeBarConfigContext})
  private bridgeBarConfig: BridgeBarConfig = {} as BridgeBarConfig;

  /**
   * Guards the dealer-label/contextType conflict warning. willUpdate() runs on
   * every property change, so without this an adopter who set both would be
   * warned again on every theme toggle or re-render.
   */
  private dealerLabelConflictWarned = false;

  override willUpdate(_changedProperties: PropertyValues): void {
    super.willUpdate(_changedProperties);
    const rawConfig = {
      applicationId: this.applicationId,
      sessionId: this.sessionId,
      deployment: this.deployment,
      partition: this.partition,
      width: this.width,
      theme: this.theme,
      language: this.language,
      userLinks: this.userLinks,
      userData: this.userData,
      dealerLabelHtml: this.dealerLabelHtml,
      useMockData: this.useMockData,
      getTracker: this.getTracker,
      contextType: this.contextType,
      sidebarVariant: this.sidebarVariant
    };

    if (!this.dealerLabelConflictWarned && hasDealerLabelConflict(rawConfig)) {
      this.dealerLabelConflictWarned = true;
      console.warn(
        "[BridgeBar] Both `contextType` and `dealerLabelHtml` were provided. " +
          "These are mutually exclusive — the Org Context Selector already " +
          "names the active dealership — so `dealerLabelHtml` is being " +
          "ignored. Remove one of the two to silence this warning."
      );
    }

    this.bridgeBarConfig = normalizeBridgeBarConfig(rawConfig);

    // Drive the i18n layer from the normalized language so `t()` resolves UI
    // strings in the requested locale. Runs in willUpdate (synchronously,
    // before render) so descendant consumers — which re-render whenever this
    // new config reference is provided — produce translated text on first
    // paint. Falls back to "en" when the language is absent or invalid.
    setLanguage(this.bridgeBarConfig.language ?? "en");
  }

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: BridgeBarConfigProviderElement;
  }
}
